import { Platform } from 'react-native';
import { signUp } from 'aws-amplify/auth';
import type { SignUpInput, SignUpOutput } from 'aws-amplify/auth';
import Constants from 'expo-constants';
import type { Environment, UseEnvironmentReturnValue } from '../hooks/useEnvironment';

export const RECAPTCHA_SIGNUP_ACTION = 'signup';

const APP_VERSION = Constants.expoConfig?.version;

const RECAPTCHA_SITE_KEYS: Partial<Record<Environment, string>> = {
    dev: '6LdQ9mItAAAAAPmOCFR390DytshROZuhDaQUwbPF',
    prod: '6LebknAtAAAAAOm1Ti1zm-RAi3X-6mIULpCcD3S0',
};
const RECAPTCHA_SCRIPT_ID = 'primary-record-recaptcha-enterprise';
const RECAPTCHA_TIMEOUT_MS = 3000;

type RecaptchaEnterprise = {
    ready: (callback: () => void) => void;
    execute: (siteKey: string, options: { action: string }) => Promise<string>;
};

declare global {
    interface Window {
        grecaptcha?: {
            enterprise?: RecaptchaEnterprise;
        };
    }
}

let recaptchaScriptPromise: Promise<void> | undefined;

const withTimeout = <T>(promise: Promise<T>, timeoutMs: number): Promise<T> =>
    new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('reCAPTCHA request timed out')), timeoutMs);

        promise.then(
            (value) => {
                clearTimeout(timeout);
                resolve(value);
            },
            (error) => {
                clearTimeout(timeout);
                reject(error);
            },
        );
    });

const loadRecaptchaScript = (siteKey: string): Promise<void> => {
    if (window.grecaptcha?.enterprise) return Promise.resolve();
    if (recaptchaScriptPromise) return recaptchaScriptPromise;

    const scriptLoadPromise = new Promise<void>((resolve, reject) => {
        const existingScript = document.getElementById(RECAPTCHA_SCRIPT_ID) as HTMLScriptElement | null;
        const script = existingScript ?? document.createElement('script');
        const handleLoad = () => resolve();
        const handleError = () => reject(new Error('Unable to load reCAPTCHA'));

        script.addEventListener('load', handleLoad, { once: true });
        script.addEventListener('error', handleError, { once: true });

        if (!existingScript) {
            script.id = RECAPTCHA_SCRIPT_ID;
            script.async = true;
            script.defer = true;
            script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`;
            document.head.appendChild(script);
        }
    });

    recaptchaScriptPromise = withTimeout(scriptLoadPromise, RECAPTCHA_TIMEOUT_MS).catch((error) => {
        document.getElementById(RECAPTCHA_SCRIPT_ID)?.remove();
        recaptchaScriptPromise = undefined;
        throw error;
    });

    return recaptchaScriptPromise;
};

const getRecaptchaSignupMetadata = async (
    environment: UseEnvironmentReturnValue,
): Promise<Record<string, string> | undefined> => {
    if (Platform.OS !== 'web' || environment === 'loading') return;

    const siteKey = RECAPTCHA_SITE_KEYS[environment];
    if (!siteKey) return;

    try {
        await loadRecaptchaScript(siteKey);

        const recaptcha = window.grecaptcha?.enterprise;
        if (!recaptcha) throw new Error('reCAPTCHA is unavailable');

        const token = await withTimeout(
            new Promise<string>((resolve, reject) => {
                recaptcha.ready(() => {
                    recaptcha.execute(siteKey, { action: RECAPTCHA_SIGNUP_ACTION }).then(resolve, reject);
                });
            }),
            RECAPTCHA_TIMEOUT_MS,
        );

        return {
            recaptchaToken: token,
            recaptchaKeyId: siteKey,
        };
    } catch {
        // Token generation is intentionally fail-open. The pre-sign-up Lambda logs the missing token.
        return;
    }
};

export const preloadRecaptcha = (environment: UseEnvironmentReturnValue): void => {
    if (Platform.OS !== 'web' || environment === 'loading') return;

    const siteKey = RECAPTCHA_SITE_KEYS[environment];
    if (!siteKey) return;

    void loadRecaptchaScript(siteKey).catch(() => undefined);
};

export const signUpWithRecaptcha = async (
    input: SignUpInput,
    environment: UseEnvironmentReturnValue,
): Promise<SignUpOutput> => {
    const recaptchaMetadata = await getRecaptchaSignupMetadata(environment);

    return signUp({
        ...input,
        options: {
            ...input.options,
            userAttributes: input.options?.userAttributes ?? {},
            clientMetadata: {
                ...input.options?.clientMetadata,
                // Sent even when token generation fails, so a sign-up that produced no reCAPTCHA
                // score can still be attributed to a platform and a build. Spreading an undefined
                // recaptchaMetadata is a no-op, so this is the only path.
                signupPlatform: Platform.OS,
                ...(APP_VERSION ? { signupAppVersion: APP_VERSION } : {}),
                ...recaptchaMetadata,
            },
        },
    });
};
